<template>
<div class="w-full grid justify-items-center">
	<panel id="documentsPanel" :title="$t('eny.documents')" addclass="mt-4 w-full max-w-[1300px]">
		<div id="eny_dashboard_overview" class="pt-8">
			<ejs-dialog
				:buttons='previewDlgButtons'
				ref="previewDialog"
				v-bind:visible="false"
				:header='previewHeader'
				:animationSettings='animationSettings'
				:showCloseIcon='false'
				:isModal='true'
				style="max-width: 1300px; left: 100px"
				:closeOnEscape="true"
				@open="onDialogOpen"
				cssClass="documentsPreviewDlg"
			>
				<previewer
					v-show="pdfURL"
					:imageURL="pdfURL"
					:style="'height:145vh;'"
				/>
				<div v-show="imageURL" class="flex justify-center items-center">
					<img :src="imageURL" />
				</div>
			</ejs-dialog>
			<uploader
				:cId="request.id"
				:title="$t('eny.file_doc_titles.upload')"
				type="undefined"
				multiple="true"
				:uploadUrl="uploadUrl"
				@on-operation-ready="reloadDocs"
				clearInterval=10
				:addToken="uploadUrl"
			>
			</uploader>
			<ejs-grid
				ref="grid"
				:allowPaging='true'
				:dataSource="documentsData"
				gridLines='Both'
				:allowTextWrap='true'
				height='570'
				allowSorting='true'
				:sortSettings='sortOptions'
				:editSettings='gridEditOptions'
				:pageSettings='pageSettings'
				:toolbar='toolbar'
			>
				<e-columns>
					<e-column field='id' headerText='ID' :isPrimaryKey='true' :visible='false'></e-column>
					<e-column :allowEditing="false" :visibleInEditForm="false" width="190" template="actionTemplate" editTemplate="emptyTemplate" />
					<e-column field='created_at' :headerText="$t('eny.date')" type="date" :format='dateFormat' width='160' :editTemplate="dateEditTemplate"></e-column>
					<e-column field='type' :headerText="$t('eny.designation')" width='40%' foreignKeyValue='label' foreignKeyField='value' :dataSource='document_types'></e-column>
					<e-column field='name' :headerText="$t('eny.document')"></e-column>
				</e-columns>
				<template #actionTemplate="{ data }">
					<div class="flex justify-start">
						<button class="e-css e-btn custombutton e-info" @click="handleDownload(data)">{{ $t('eny.download') }}</button>
						<button v-if="isPdf(data.name) || isImage(data.name)" class="e-css e-btn custombutton e-info" @click="handlePreview(data)">{{ $t('eny.preview') }}</button>
					</div>
				</template>
				<template #emptyTemplate>
				</template>
			</ejs-grid>
		</div>
	</panel>
</div>
</template>

<script>
import { GridComponent, ColumnsDirective, ColumnDirective } from '@syncfusion/ej2-vue-grids';
import { DatePickerComponent } from "@syncfusion/ej2-vue-calendars";
import { DataManager, WebApiAdaptor } from "@syncfusion/ej2-data";
import { DialogComponent } from "@syncfusion/ej2-vue-popups";
import Uploader from "../../Base/Uploader.vue";
import Panel from "../../Base/Panel.vue";
import Previewer from '../../Base/Previewer.vue'
import useNotification from "../../hooks/useNotification";
import useUtils from '../../hooks/useUtils';
import { createApp } from 'vue'
import { mapGetters } from 'vuex';

class DocumentsAdaptor extends WebApiAdaptor
{
	constructor(component) {
		super();
		this.component = component;
	}

	beforeSend(dm, request)
	{
		const xsrfEl = document.getElementsByName('csrf-token')[0];
		request.headers.append("X-CSRF-TOKEN", xsrfEl.content);
		const kc = this.component.$keycloak;
		request.headers.append("Authorization", `Bearer ${kc.token}`);
		// Add userId parameter as custom header.
		const userId = (this.component.$route.params.uid ? this.component.$route.params.uid : (this.component.user ? this.component.user.user_id : "") );
		request.headers.append("X-UID", userId);
	}
}

const appObj = createApp({});
const datePickerComp = appObj.component('dateEditTemplate', {
	template: '<ejs-datepicker :id="data.column.field" :value="data[data.column.field]" format="dd.MM.yyyy"></ejs-datepicker>',
	data() {return {}},
	components: {
		"ejs-datepicker": DatePickerComponent,
	}
});

export default {
	name: "DashboardDocuments",
	components: {
		'panel': Panel,
		'ejs-grid': GridComponent,
		'e-columns': ColumnsDirective,
		'e-column': ColumnDirective,
		'ejs-dialog': DialogComponent,
		'uploader': Uploader,
		'previewer': Previewer,
	},
	props: ['allowDelete'],
	setup() {
		const { showNotification } = useNotification();
		const { sleep, sendToBrowser } = useUtils();
		return {
			showNotification,
			sleep,
			sendToBrowser,
		}
	},
	data() {
		return {
			dateEditTemplate: function() {
				return {
					template: datePickerComp
				};
			},
			dateFormat: { type: 'date', format: this.$t('eny.date_time_format')},
			documentsData: [],
			// controls' data
			document_types: JSON.parse(this.$t('eny.document_types')),
			pageSettings: {pageSizes: true, pageCount: 4, pageSize: 10},
			toolbar: ['Edit', 'Delete', 'Update', 'Cancel', 'Search'],
			sortOptions: { columns: [{ field: 'created_at', direction: 'Descending' }] },
			gridEditOptions: {
				allowAdding: true,
				allowEditing: true,
				allowDeleting: this.allowDelete,
			},
			previewHeader: '',
			animationSettings: { effect: 'None' },
			previewDlgButtons: [
				{ click: this.closePreviewDlgBtnClick, buttonModel: { content: this.$t('eny.close_btn'), isPrimary: true } }
			],
			pdfURL: null,
			imageURL: null,
		};
	},
	mounted() {
		this.setDocumentsData()
	},
	methods: {
		closePreviewDlgBtnClick: function() {
			this.$refs.previewDialog.hide();
		},
		onDialogOpen() {
			this.$refs.previewDialog.$el.focus();
		},
		isPdf(fileName) {
			return fileName.toLowerCase().endsWith('.pdf')
		},
		isImage(fileName) {
			const ext = fileName.split('.').pop().toLowerCase();
			const imageExts = ['jpg','jpeg','png','gif','bmp','svg','webp','tiff'];
			return imageExts.includes(ext);
		},
		handleDownload(row) {
			axios
				.get(`/customer/document/${row.id}`, { responseType: 'blob' })
				.then((response) => this.sendToBrowser(response.data, row.name))
				.catch(alert);
		},
		handlePreview(row) {
			axios
				.get(`/customer/document/${row.id}`, { responseType: 'blob' })
				.then((response) => {
					if (response.data.type === 'application/pdf') {
						this.pdfURL = URL.createObjectURL(response.data);
						this.imageURL = null;
					} else if (response.data.type.startsWith('image/')) {
						this.imageURL = URL.createObjectURL(response.data);
						this.pdfURL = null;
					}
					if (this.pdfURL || this.imageURL) {
						this.previewHeader = row.name;
						this.$refs.previewDialog.show();
					}
				})
				.catch(alert);
		},
		reloadDocs : function() {
			this.$refs.grid.refresh();
		},
		setDocumentsData() {
			this.documentsData = new DataManager({
				url: "/customer/documents",
				adaptor: new DocumentsAdaptor(this),
			})
		}
	},
	computed: {
		...mapGetters('customer', ['request', 'success', 'message']),
		...mapGetters('user', ['user']),
		isCustomer: function() {
			return this.$keycloak.hasRealmRole('customer');
		},
		uploadUrl: function () {
			return this.isCustomer ? '/customer/upload/save/auth' : ''
		},
	},
	watch: {
		message(value, oldValue) {
			if (value!=='') {
				const type=this.success ? 'info' : 'error';
				this.showNotification(value, type, 5);
			}
		},
		user(value) {
			if (value.user_id != undefined) {
				this.setDocumentsData()
			}
		}
	}
}
</script>

<style>
.e-grid .e-rowcell{
	font-size: 12px;
}
.custombutton {
	margin-right: 4px;
	padding: 4px 8px;
	font-size: 13px;
}
.documentsPreviewDlg .e-dlg-header {
	text-align: center !important;
	width: 100% !important;
}
</style>
